import { useQueryClient } from "@tanstack/react-query";

export const useInvalidateAll = async () => {
  const queryClient = useQueryClient();
  Promise.all([
    queryClient.invalidateQueries({
      queryKey: ["availableBalances"],
      exact: false,
    }),
    queryClient.invalidateQueries({
      queryKey: ["userTransactionHistory"],
      exact: false,
    }),
    queryClient.invalidateQueries({ queryKey: ["userOrders"], exact: false }),
    queryClient.invalidateQueries({ queryKey: ["cryptoAssets"], exact: false }),
    queryClient.invalidateQueries({ queryKey: ["subAccounts"], exact: false }),
  ]);
};

export const refreshBalanceQueries = async (queryClient, delayMs = 500) => {
  if (!queryClient) {
    console.error("queryClient is required for refreshBalanceQueries");
    return;
  }

  try {
    if (delayMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }

    await queryClient.refetchQueries({
      queryKey: ["portfolioEstimateAssets"],
      exact: false,
      type: "active",
    });

    await new Promise((resolve) => setTimeout(resolve, 50));

    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["portfolioEstimateAssets"],
        exact: false,
      }),
      queryClient.invalidateQueries({
        queryKey: ["availableBalances"],
        exact: false,
      }),
      queryClient.invalidateQueries({
        queryKey: ["usableAssets"],
        exact: false,
      }),
      queryClient.invalidateQueries({
        queryKey: ["walletBalance"],
        exact: false,
      }),
      queryClient.invalidateQueries({
        queryKey: ["availableAssets"],
        exact: false,
      }),
      queryClient.invalidateQueries({
        queryKey: ["fiatBalance"],
        exact: false,
      }),
      queryClient.invalidateQueries({
        queryKey: ["fiatAssets"],
        exact: false,
      }),
    ]);

    await Promise.all([
      queryClient.refetchQueries({
        queryKey: ["availableBalances"],
        exact: false,
        type: "active",
      }),
      queryClient.refetchQueries({
        queryKey: ["usableAssets"],
        exact: false,
        type: "active",
      }),
      queryClient.refetchQueries({
        queryKey: ["walletBalance"],
        exact: false,
        type: "active",
      }),
      queryClient.refetchQueries({
        queryKey: ["cryptoAssets"],
        exact: false,
        type: "active",
      }),
      queryClient.refetchQueries({
        queryKey: ["userOrders"],
        exact: false,
        type: "active",
      }),
    ]);
  } catch (error) {
    console.error("Error refreshing balance queries:", error);
  }
};

/**
 * After business wallet trades / sends, refresh v2 business wallet cache.
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {string | null | undefined} businessId
 */
export const refreshBusinessWalletQueries = async (queryClient, businessId) => {
  if (!queryClient) {
    console.error("queryClient is required for refreshBusinessWalletQueries");
    return;
  }
  if (!businessId) {
    return;
  }
  try {
    await queryClient.invalidateQueries({
      queryKey: ["businessWallet", businessId],
      exact: false,
    });
    await queryClient.invalidateQueries({
      queryKey: ["businessAvailableAssets", businessId],
      exact: false,
    });
    await queryClient.invalidateQueries({
      queryKey: ["businessAvailableBalances", businessId],
      exact: false,
    });
  } catch (error) {
    console.error("Error refreshing business wallet queries:", error);
  }
};
